import { FC, useState, useEffect, useRef } from 'react';
import * as React from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk';
import { AlertPanel } from '../AlertPanel';
import { AlertPanelMobile } from 'search/AlertPanelMobile';
import { SeoLinksComponent } from '../SeoLinks';
import {
    AD_UNIT_PAGE,
    AD_UNIT_ROOTS,
    DFP_AD_UNIT,
    DfpTargeting,
    AdvertProvider,
    AdvertSlotDfp,
    getPageSectionForAdUnit
} from '@zoopla/adverts';
import { SeoLinks } from '../SeoLinks/SeoLinks.types';
import { AlertCtaModal } from '../AlertCtaModal';
import { breakpoints } from '../common/constants';
import { InnerWrapper, SearchResultsWrapper, SideBar } from './Layout.styles';
import { Links } from '../SearchContext';
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn';
import SearchAndFitersWrapper from 'search/SearchAndFiltersWrapper/SearchAndFiltersWrapper';
import { useContainerMeasurement } from '@zoopla/dom-util';
import { ADBRIDG_CLASS } from '../../../zoopla/libs/adverts/src/lib/types';

const StickyContainer = styled.div<{ height: string }>(
    ({ height }) => css`
        height: ${height};
    `
);

const Sticky = styled.div<{ disabled?: boolean }>(
    ({ disabled }) =>
        !disabled &&
        css`
            position: sticky;
            top: 0;
            width: 100%;
        `
);

const Main = styled.main`
    width: 100%;
`;

export type SearchResultsLayout = {
    dfpAdTargeting: DfpTargeting;
    seoAccordions: SeoLinks[];
    numberOfRegularListings: number;
    pageNumber?: number;
    links: Links;
    children: React.ReactNode;
};

const SearchResultsLayout: FC<SearchResultsLayout & WithOptimizelyProps> = ({
    dfpAdTargeting,
    seoAccordions,
    children,
    pageNumber,
    links
}) => {
    const SKYSCRAPER_AD_HEIGHT = 600;
    const isLoggedIn = useIsLoggedIn();
    const { route } = useRouter();

    const adUnitPage = getPageSectionForAdUnit(route);

    const [adProviderVal, updateAdProviderVal] = useState({
        showPlaceHolderContent: false,
        reportMode: false,
        targeting: {
            ...dfpAdTargeting,
            signed_in_status: !!isLoggedIn ? 'signed in' : 'signed out'
        },
        pageNumber,
        adBridgPage: adUnitPage
    });

    useEffect(() => {
        updateAdProviderVal({
            showPlaceHolderContent: false,
            reportMode: false,
            targeting: {
                ...dfpAdTargeting,
                signed_in_status: !!isLoggedIn ? 'signed in' : 'signed out'
            },
            pageNumber,
            adBridgPage: adUnitPage
        });
    }, [dfpAdTargeting, isLoggedIn, pageNumber, adUnitPage]);

    const sidebarRef = useRef<HTMLDivElement>(null);
    const stickyContainerRef = useRef<HTMLDivElement>(null);
    const [, sidebarHeight] = useContainerMeasurement(sidebarRef);
    const [stickyContainerHeight, setStickyContainerHeight] = useState('auto');
    const [disableSticky, setDisableSticky] = useState(false);

    useEffect(() => {
        const stickyContainerYPosition =
            stickyContainerRef.current?.offsetTop || 0;
        const numberOfSkyscraperAds = 2;
        const margin = 32;
        const verticalSpace = sidebarHeight - stickyContainerYPosition;

        setDisableSticky(
            verticalSpace <=
                (SKYSCRAPER_AD_HEIGHT + margin) * numberOfSkyscraperAds
        );
        setStickyContainerHeight(
            disableSticky ? 'auto' : Math.floor(verticalSpace / 2) + 'px'
        );
    }, [sidebarHeight, stickyContainerHeight, disableSticky]);

    return (
        <AdvertProvider value={adProviderVal}>
            <SearchAndFitersWrapper>
                <SearchResultsWrapper suppressHydrationWarning={true}>
                    <AlertPanelMobile links={links} />
                    <InnerWrapper>
                        <Main data-testid="search-content">{children}</Main>
                        <SideBar data-testid="search-sidebar" ref={sidebarRef}>
                            <AlertPanel links={links} />
                            <AdvertSlotDfp
                                position={0}
                                showOnly={['>', breakpoints.lsrpSidebar]}
                                sizes={[[300, 16], 'fluid']}
                                adUnitCode={[
                                    AD_UNIT_ROOTS.TEXT_LINK,
                                    getPageSectionForAdUnit(route),
                                    AD_UNIT_PAGE.LSRP
                                ]}
                                adbridgClass={ADBRIDG_CLASS.TEXT_LINK_LSRP}
                            />
                            <AdvertSlotDfp
                                position={0}
                                showOnly={['>', breakpoints.lsrpSidebar]}
                                sizes={[[300, 250]]}
                                adUnitCode={[AD_UNIT_ROOTS.MPU, adUnitPage]}
                                adbridgClass={ADBRIDG_CLASS.MPU}
                            />
                            <AdvertSlotDfp
                                position={0}
                                showOnly={['>', breakpoints.lsrpSidebar]}
                                sizes={[[300, 120], 'fluid']}
                                adUnitCode={DFP_AD_UNIT.VALUATION_BOOSTER}
                                adbridgClass={ADBRIDG_CLASS.VALUATION_BOOSTER}
                            />
                            <AdvertSlotDfp
                                position={1}
                                showOnly={['>', breakpoints.lsrpSidebar]}
                                sizes={[[300, 250]]}
                                adUnitCode={[AD_UNIT_ROOTS.MPU_2, adUnitPage]}
                                adbridgClass={ADBRIDG_CLASS.MPU_2}
                            />

                            {!!seoAccordions.length && <SeoLinksComponent />}

                            <StickyContainer
                                height={stickyContainerHeight}
                                ref={stickyContainerRef}
                            >
                                <Sticky disabled={disableSticky}>
                                    <AdvertSlotDfp
                                        position={0}
                                        showOnly={[
                                            '>',
                                            breakpoints.lsrpSidebar
                                        ]}
                                        sizes={[
                                            [120, SKYSCRAPER_AD_HEIGHT],
                                            [160, SKYSCRAPER_AD_HEIGHT],
                                            [300, SKYSCRAPER_AD_HEIGHT]
                                        ]}
                                        adUnitCode={[
                                            AD_UNIT_ROOTS.SKY_SCRAPER,
                                            adUnitPage
                                        ]}
                                        adbridgClass={ADBRIDG_CLASS.SKYSCRAPER}
                                    />
                                </Sticky>
                            </StickyContainer>
                            <StickyContainer height={stickyContainerHeight}>
                                <Sticky disabled={disableSticky}>
                                    <AdvertSlotDfp
                                        position={1}
                                        showOnly={[
                                            '>',
                                            breakpoints.lsrpSidebar
                                        ]}
                                        sizes={[
                                            [120, SKYSCRAPER_AD_HEIGHT],
                                            [160, SKYSCRAPER_AD_HEIGHT],
                                            [300, SKYSCRAPER_AD_HEIGHT]
                                        ]}
                                        adUnitCode={[
                                            AD_UNIT_ROOTS.SKY_SCRAPER_2,
                                            adUnitPage
                                        ]}
                                        adbridgClass={
                                            ADBRIDG_CLASS.SKYSCRAPER_2
                                        }
                                    />
                                </Sticky>
                            </StickyContainer>
                        </SideBar>
                    </InnerWrapper>
                </SearchResultsWrapper>
                <AlertCtaModal links={links} />
            </SearchAndFitersWrapper>
        </AdvertProvider>
    );
};

export default withOptimizely(SearchResultsLayout);
